import React from 'react'
import { Select, Spin, Space, Tag } from 'antd'
import { useTranslation } from 'react-i18next'
import useShelfSearch from '@hooks/shared/shelves/useShelfSearch'
import shelfService from '@services/operator/shelfService'

/**
 * 儲位選擇組件 (Async Search)
 */
export default function ShelfSelect({
  value,
  onChange,
  placeholder,
  initialOption,
  ...selectProps
}) {
  const { t } = useTranslation()
  const {
    shelves,
    loading,
    searchKeyword,
    inputValue,
    minSearchLength,
    searchShelves,
    clearShelves
  } = useShelfSearch(shelfService)

  // 合併初始選項與搜尋結果
  const mergedOptions = React.useMemo(() => {
    if (!initialOption || !initialOption.value) return shelves;
    
    // 如果初始選項不在搜尋結果中，將其加入
    const exists = shelves.some(s => s.value === initialOption.value);
    if (!exists) {
      return [initialOption, ...shelves];
    }
    return shelves;
  }, [shelves, initialOption]);

  const HighlightText = ({ text, highlight }) => {
    if (!highlight || !highlight.trim()) {
      return <span>{text}</span>;
    }
    const regex = new RegExp(`(${highlight})`, 'gi');
    const parts = text.split(regex);
    return (
      <span>
        {parts.map((part, i) => (
          regex.test(part) ? (
            <span key={i} style={{ backgroundColor: '#fff566', padding: '0 2px', borderRadius: '2px' }}>
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          )
        ))}
      </span>
    );
  };

  const handleClear = () => {
    clearShelves()
    onChange?.(null)
  }

  return (
    <Select
      showSearch
      allowClear
      placeholder={placeholder}
      value={value}
      loading={loading}
      options={mergedOptions}
      onSearch={searchShelves}
      onChange={onChange}
      onClear={handleClear}
      filterOption={false}
      notFoundContent={
        loading ? (
          <div style={{ textAlign: 'center', padding: '8px' }}>
            <Spin size="small" />
          </div>
        ) : (!inputValue || inputValue.length < minSearchLength) ? (
          <div style={{ padding: '8px', color: '#999', textAlign: 'center' }}>
            {t('common.select.min_length_hint', { count: minSearchLength })}
          </div>
        ) : (
          <div style={{ padding: '8px', color: '#999', textAlign: 'center' }}>
            {t('common.select.no_results')}
          </div>
        )
      }
      optionRender={(option) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <HighlightText text={option.data.location || option.data.label} highlight={searchKeyword} />
          <Space>
            {option.data.warehouse && <Tag size="small">{option.data.warehouse.name}</Tag>}
            {option.data.temperature && <Tag color="blue" size="small">{t(`common.shelf_temperature.${option.data.temperature}`)}</Tag>}
          </Space>
        </div>
      )}
      {...selectProps}
    />
  )
}
